<script lang="ts">
  import * as _ from "lodash";
  import moment from "moment";
  import { HttpPromise } from "@shared/svelte/Modules/ApiService";
  import Flash from "@shared/services/flashv2.js";
  import type { OrderMessage, SortedMessages, ViewerContext } from "@shared/types/OrderMessage";
  import Form from "./Form.svelte";
  import MessagesStore from "../../../Stores/OrderMessagesStore";
  import MessagesViewerStore from "../../../Stores/OrderMessagesViewerStore";
  import { afterUpdate, onDestroy, getContext } from "svelte";
  import {
    orderMessageThreadDate,
    orderMessageDatetime,
  } from "@shared/svelte/Components/DateFormatter/LocaleString.svelte";
  import FizzStyles from "../../FizzStyles.svelte";

  export let orderId: number;
  export let messageThreadId: number;
  export let extraClasses: string = "";
  export let compactMode = false;

  const sleep = (ms: number) => {
    return new Promise(resolve => setTimeout(resolve, ms));
  };

  const scrollToLatestMessage = async () => {
    try {
      let nodes;
      await sleep(1);
      nodes = document.querySelectorAll(".messages-box");
      nodes.forEach(el => {
        el.scrollTo(0, el.scrollHeight);
      });
    } catch (e) {}
  };

  afterUpdate(() => {
    scrollToLatestMessage();
  });

  let messages: OrderMessage[];
  let sortedMessages: SortedMessages;
  let token: string;
  let repId: string;
  token = getContext("token");
  repId = getContext("repId");
  let viewerContext: ViewerContext;
  const messagesUnsubscribe = MessagesStore.subscribe((msgs: OrderMessage[]) => {
    messages = msgs;
    scrollToLatestMessage();
  });
  $: sortedMessages = sortMessagesByDate(messages);
  const messagesViewerUnsubscribe = MessagesViewerStore.subscribe((viewer: ViewerContext) => {
    viewerContext = viewer;
  });
  onDestroy(() => {
    messagesUnsubscribe();
    messagesViewerUnsubscribe();
  });

  const fetchMessages = async () => {
    try {
      let response = await HttpPromise({
        method: "GET",
        url:
          `api/orders/${orderId}/message_threads` +
          (token ? `?token=${token}` : "") +
          (repId ? `&rep_id=${repId}` : ""),
      });
      if (response.threads.length) {
        messages = response.threads[0].messages;
      } else {
        messages = [];
      }
      MessagesStore.set(messages);
      MessagesViewerStore.set(response.self);
    } catch (_) {
      Flash.now({
        type: "error",
        message: "There was a problem loading messages",
      });
    }
  };
  fetchMessages();

  const avatarInitial = (sender: OrderMessage["sender"]): string => {
    return sender?.type === viewerContext.type && sender.id === viewerContext.id ? "1" : "2";
  };
  const sortMessagesByDate = (messages: OrderMessage[]): SortedMessages => {
    messages = messages.sort(message => message.created_at);
    let messagesArray = messages.map(message => {
      // @ts-ignore
      return { ...message, created_at: moment(message.created_at) };
    });

    return _.groupBy(messagesArray, (m: any) => orderMessageThreadDate(m.created_at));
  };
</script>

<style>
  .messages-box {
    min-height: 250px;
    max-height: 500px;
  }
  .scrollable {
    overflow-y: scroll;
  }
</style>

<FizzStyles>
  <div id="order-detail-messages" class={extraClasses}>
    <div class="fizz-card" class:fizz-bordered={!compactMode}>
      {#if !compactMode}
        <p class="fizz-text-medium fizz-text-strong fizz-inset-square-16">Messages</p>
      {/if}
      {#if sortedMessages && Object.keys(sortedMessages).length}
        <div
          class="fizz-inset-square-16 fizz-flow-16 scrollable messages-box"
          class:fizz-border-top={!compactMode}
        >
          {#each Object.keys(sortedMessages) as date}
            <p class="fizz-text-subdued fizz-text-small fizz-text-center">
              {date}
            </p>
            {#each sortedMessages[date] as message}
              <div class="fizz-flex-row">
                <div
                  class="fizz-avatar-initial-{avatarInitial(
                    message.sender,
                  )} fizz-flex-no-shrink fizz-align-self-start"
                >
                  {message.sender.name.charAt(0)}
                </div>
                <div>
                  <p>
                    <strong>{message.sender.name}</strong>
                    <span class="fizz-text-subdued fizz-text-small"
                      ><strong>{orderMessageDatetime(message.created_at)}</strong></span
                    >
                  </p>
                  <p>{message.content}</p>
                </div>
              </div>
            {/each}
          {/each}
          <div id="latest" />
        </div>
      {:else}
        <div class="fizz-border-top fizz-inset-square-16 fizz-grid-center messages-box">
          <p class="fizz-text-subdued fizz-text-small fizz-text-center">
            Send a message about substitutions, delivery day confirmation, or just a friendly hello.
          </p>
        </div>
      {/if}

      {#if !compactMode}
        <div class="fizz-border-top fizz-inset-square-16 fizz-hide-on-small fizz-relative">
          <Form {orderId} {messageThreadId} />
        </div>
      {/if}
    </div>
  </div>
</FizzStyles>
